<template>
  <div class="profile-body">
    <ProfilePageHeader title="Моё тело" />

    <!-- Основные параметры -->
    <div class="form-card">
      <!-- Пол -->
      <label class="field-label">ПОЛ</label>
      <div class="pill-row">
        <button class="pill" :class="{ active: authStore.user?.sex === 'female' }" @click="setSex('female')">Женский</button>
        <button class="pill" :class="{ active: authStore.user?.sex === 'male' }" @click="setSex('male')">Мужской</button>
      </div>

      <!-- Дата рождения -->
      <label class="field-label">ДАТА РОЖДЕНИЯ</label>
      <div class="date-selects">
        <select v-model="birthDay" class="date-select">
          <option value="" disabled>День</option>
          <option v-for="d in days" :key="d" :value="d">{{ d }}</option>
        </select>
        <select v-model="birthMonth" class="date-select date-select-month">
          <option value="" disabled>Месяц</option>
          <option v-for="(m, i) in monthNames" :key="i" :value="i + 1">{{ m }}</option>
        </select>
        <select v-model="birthYear" class="date-select">
          <option value="" disabled>Год</option>
          <option v-for="y in years" :key="y" :value="y">{{ y }}</option>
        </select>
      </div>

      <!-- Рост -->
      <label class="field-label">РОСТ</label>
      <input
        v-model.number="heightCm"
        type="number"
        inputmode="numeric"
        class="form-input"
        placeholder="см"
      />
      <p v-if="heightStatus" class="save-status">{{ heightStatus }}</p>
    </div>

    <!-- Вес -->
    <div class="form-card">
      <label class="field-label">ВЕС</label>

      <!-- Пока веса нет — обычное поле, как у роста: барабан начинается с 70 кг,
           и человеку с большим весом пришлось бы прокручивать сотни делений.
           Как только вес появился, удобнее барабан: он стартует от прошлого
           значения, и менять надо на десятые доли. -->
      <input
        v-if="!hasWeight"
        :value="weightInput"
        @input="onWeightInput"
        type="text"
        inputmode="decimal"
        class="form-input"
        placeholder="кг"
      />
      <p v-if="!hasWeight && weightError" class="save-status">{{ weightError }}</p>
      <p v-else-if="!hasWeight && weightStatus" class="save-status">{{ weightStatus }}</p>

      <div v-if="hasWeight" class="input-btn-wrap" @click="openWeightPopup">
        <input
          :value="parseFloat(authStore.user.weight_kg).toFixed(1) + ' кг'"
          type="text"
          class="form-input"
          placeholder="кг"
          readonly
        />
        <button class="input-inner-btn" type="button">Обновить</button>
      </div>

      <!-- Предыдущие взвешивания -->
      <template v-if="weightHistory.length > 0">
        <label class="field-label">ПРЕДЫДУЩИЕ ВЗВЕШИВАНИЯ</label>
        <div class="history-list">
          <div v-for="(w, i) in weightHistory" :key="i" class="history-row">
            <span class="history-value">{{ parseFloat(w.weight_kg).toFixed(1) }} кг</span>
            <span class="history-date">{{ formatDate(w.logged_at) }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Доля жировой ткани -->
    <div class="form-card">
      <div class="field-label-with-info">
        <label class="field-label">ДОЛЯ ЖИРОВОЙ ТКАНИ</label>
        <button class="info-btn" @click="showMethodPopup = true">
          <svg width="18" height="18" viewBox="0 0 22 22" fill="none">
            <circle cx="11" cy="11" r="10" stroke="#B98A5E" stroke-width="1.5"/>
            <text x="11" y="15.5" text-anchor="middle" fill="#B98A5E" font-size="13" font-weight="600" font-family="inherit">i</text>
          </svg>
        </button>
      </div>

      <template v-if="lastBodyFat !== null">
        <div class="input-btn-wrap" @click="openFatCalcPopup()">
          <input
            :value="lastBodyFat + '%'"
            type="text"
            class="form-input"
            readonly
          />
          <button class="input-inner-btn" type="button">Рассчитать</button>
        </div>
      </template>
      <template v-else>
        <button class="btn-primary" @click="openFatCalcPopup()">Рассчитать</button>
      </template>

      <!-- История расчётов -->
      <template v-if="fatHistory.length > 0">
        <label class="field-label">ПРЕДЫДУЩИЕ РАСЧЁТЫ</label>
        <div class="history-list">
          <div v-for="(f, i) in fatHistory" :key="i" class="history-row">
            <span class="history-value">{{ parseFloat(f.body_fat_percent).toFixed(1) }}%</span>
            <span class="history-date">{{ formatDate(f.logged_at) }}</span>
          </div>
        </div>
      </template>
    </div>

    <!-- Назад в Профиль -->
    <button class="btn-primary btn-back" @click="$router.push('/profile')">
      Назад в Профиль
    </button>

    <!-- Body Fat Calculator Popup -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showFatCalcPopup" class="popup-overlay" @click.self="closeFatCalcPopup">
          <div class="popup-backdrop" @click="closeFatCalcPopup" />
          <div class="popup-sheet">
            <div class="popup-header">
              <div class="popup-handle" />
              <div class="popup-title-row">
                <h3 class="popup-title">Калькулятор доли жировой ткани</h3>
                <button @click="closeFatCalcPopup" class="popup-close">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="popup-body">
              <div class="calc-fields">
                <label class="field-label">ОБХВАТ ШЕИ</label>
                <input
                  v-model.number="neck"
                  type="number"
                  inputmode="decimal"
                  class="form-input"
                  placeholder="см"
                />

                <label class="field-label">ОБХВАТ ТАЛИИ</label>
                <input
                  v-model.number="waist"
                  type="number"
                  inputmode="decimal"
                  class="form-input"
                  placeholder="см"
                />

                <template v-if="isFemale">
                  <label class="field-label">ОБХВАТ БЁДЕР</label>
                  <input
                    v-model.number="hip"
                    type="number"
                    inputmode="decimal"
                    class="form-input"
                    placeholder="см"
                  />
                </template>
              </div>

              <button
                class="btn-primary"
                :disabled="!canCalculate || savingFat"
                @click="calculateAndSaveBodyFat"
              >
                {{ savingFat ? 'Сохранение...' : 'Рассчитать' }}
              </button>

              <!-- Результат внутри попапа -->
              <template v-if="lastBodyFat !== null">
                <div class="calc-result">
                  <label class="field-label">РЕЗУЛЬТАТ</label>
                  <div class="fat-result">
                    <span class="fat-result-value">{{ lastBodyFat }}%</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Методика Popup -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showMethodPopup" class="popup-overlay" @click.self="closeMethodPopup">
          <div class="popup-backdrop" @click="closeMethodPopup" />
          <div class="popup-sheet">
            <div class="popup-header">
              <div class="popup-handle" />
              <div class="popup-title-row">
                <h3 class="popup-title">Формула расчёта доли жировой ткани</h3>
                <button @click="closeMethodPopup" class="popup-close">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
            <div class="popup-body">
              <div class="popup-text">
                <p>Самая достоверная формула для расчёта процента жира по обхватам тела — это <strong>формула Военно-морских сил США</strong> (U.S. Navy Body Fat Formula).</p>
                <p>Она была разработана в 1984 году докторами Ходждоном и Беккетом в Исследовательском центре здоровья ВМС США.</p>
                <p>Погрешность этого метода составляет около 3–4%. Хотя формула уступает медицинским золотым стандартам (например, DEXA-сканированию или гидростатическому взвешиванию), она считается лучшим инструментом для самостоятельного отслеживания динамики похудения или набора массы.</p>
                <p class="popup-subtitle">Как правильно снимать мерки</p>
                <p>Для получения корректного результата критически важно делать замеры правильно. Лента должна плотно прилегать к коже, но не сдавливать её. Лучше всего проводить измерения утром натощак.</p>
                <p><strong>Рост:</strong> Измеряется без обуви, спина прямая.</p>
                <p><strong>Шея:</strong> Сантиметровая лента должна проходить чуть ниже гортани (кадыка) и быть строго параллельна полу. Плечи и шея расслаблены.</p>
                <p><strong>Талия:</strong></p>
                <ul>
                  <li><em>У мужчин:</em> измеряется строго на уровне пупка.</li>
                  <li><em>У женщин:</em> измеряется в самом узком месте живота (обычно на несколько сантиметров выше пупка). Живот должен быть в естественном, расслабленном состоянии после нормального выдоха.</li>
                </ul>
                <p><strong>Бёдра</strong> (только для женщин): Измеряются по самой выступающей части ягодиц. Лента должна находиться в строго горизонтальной плоскости.</p>
              </div>
              <button @click="closeMethodPopup" class="btn-primary">Понятно</button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Weight Popup -->
    <Teleport to="body">
      <Transition name="fade">
        <div v-if="showWeightPopup" class="popup-overlay" @click.self="showWeightPopup = false">
          <div class="popup-backdrop" @click="showWeightPopup = false" />
          <div class="popup-sheet popup-sheet-compact">
            <div class="popup-handle" />
            <p class="weight-popup-title">Текущий вес</p>

            <!-- Попап открывается только когда вес уже задан: первый ввод идёт
                 обычным полем на самом экране, как у роста. -->
            <div class="weight-picker-row">
              <WheelPicker
                :items="weightItems"
                v-model="selectedWeight"
                style="flex: 1"
              />
              <span class="weight-picker-unit">кг</span>
            </div>

            <button
              @click="saveWeight"
              :disabled="savingWeight || !weightValue"
              class="btn-primary"
            >
              {{ savingWeight ? 'Сохранение...' : 'Сохранить вес' }}
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import ProfilePageHeader from '../components/ProfilePageHeader.vue'
import { ref, computed, watch, onMounted } from 'vue'
import { useAuthStore } from '../store/auth.js'
import api from '../services/api.js'
import WheelPicker from '../components/WheelPicker.vue'

const authStore = useAuthStore()

// ── Пол ──
async function setSex(val) {
  try { await authStore.updateProfile({ sex: val }) } catch { /* silent */ }
}

// ── Дата рождения (3 селекта + автосохранение) ──
const currentYear = new Date().getFullYear()
const years = []
// Минимальный возраст 10 лет — не показываем слишком свежие годы
for (let y = currentYear - 10; y >= currentYear - 100; y--) years.push(y)

const monthNames = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь']

const days = []
for (let d = 1; d <= 31; d++) days.push(d)

const existingBD = authStore.user?.birth_date ? new Date(authStore.user.birth_date) : null
const birthDay = ref(existingBD ? existingBD.getUTCDate() : '')
const birthMonth = ref(existingBD ? existingBD.getUTCMonth() + 1 : '')
const birthYear = ref(existingBD ? existingBD.getUTCFullYear() : '')

const birthDateISO = computed(() => {
  if (!birthDay.value || !birthMonth.value || !birthYear.value) return ''
  const mm = String(birthMonth.value).padStart(2, '0')
  const dd = String(birthDay.value).padStart(2, '0')
  return `${birthYear.value}-${mm}-${dd}`
})

let birthTimer = null
watch(birthDateISO, (val) => {
  clearTimeout(birthTimer)
  if (!val) return
  birthTimer = setTimeout(async () => {
    try { await authStore.updateProfile({ birth_date: val }) } catch { /* silent */ }
  }, 800)
})

// ── Рост (автосохранение) ──
const heightCm = ref(authStore.user?.height_cm || null)
const heightStatus = ref('')
let heightTimer = null

watch(heightCm, (val) => {
  heightStatus.value = ''
  clearTimeout(heightTimer)
  if (!val || val < 50 || val > 250) return
  heightTimer = setTimeout(async () => {
    try {
      heightStatus.value = 'Сохранение...'
      await authStore.updateProfile({ height_cm: val })
      heightStatus.value = 'Сохранено'
      setTimeout(() => { if (heightStatus.value === 'Сохранено') heightStatus.value = '' }, 2000)
    } catch {
      heightStatus.value = 'Ошибка сохранения'
    }
  }, 800)
})

// ── Вес ──
const showWeightPopup = ref(false)
const savingWeight = ref(false)
const weightHistory = ref([])

// Диапазон один и тот же для барабана и для ручного ввода, чтобы набранное
// числом значение потом нашлось в барабане.
const WEIGHT_MIN = 30, WEIGHT_MAX = 200
const weightItems = []
for (let w = WEIGHT_MIN * 10; w <= WEIGHT_MAX * 10; w++) {
  weightItems.push((w / 10).toFixed(1))
}

const defaultWeight = authStore.user?.weight_kg
  ? parseFloat(authStore.user.weight_kg).toFixed(1)
  : '70.0'
const selectedWeight = ref(defaultWeight)
// Вес уже известен → барабан от прошлого значения. Ещё нет → ввод цифрами:
// барабан стартовал бы с 70 кг, и при большом весе это сотни делений прокрутки.
const hasWeight = computed(() => !!authStore.user?.weight_kg)
const weightInput = ref('')

function onWeightInput(e) {
  // Запятая и точка равноправны, остальное не пропускаем; вторая точка
  // отбрасывается, иначе parseFloat молча обрежет хвост.
  let v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '')
  const parts = v.split('.')
  if (parts.length > 2) v = parts[0] + '.' + parts.slice(1).join('')
  weightInput.value = v
  e.target.value = v
}

// Единственный источник значения для сохранения — что барабан, что поле ввода
const weightValue = computed(() => {
  const n = hasWeight.value ? parseFloat(selectedWeight.value) : parseFloat(weightInput.value)
  if (!Number.isFinite(n) || n < WEIGHT_MIN || n > WEIGHT_MAX) return null
  // Барабан оперирует десятыми, поэтому и набранное вручную округляем до них —
  // иначе «85,55» сохранится, а в барабане потом окажется 85.6.
  return Math.round(n * 10) / 10
})

const weightError = computed(() => {
  if (hasWeight.value || !weightInput.value) return ''
  const n = parseFloat(weightInput.value)
  if (!Number.isFinite(n)) return 'Введите вес числом'
  if (n < WEIGHT_MIN || n > WEIGHT_MAX) return `Вес должен быть от ${WEIGHT_MIN} до ${WEIGHT_MAX} кг`
  return ''
})

// Первый ввод сохраняется сам, как рост: отдельной кнопки у поля нет.
const weightStatus = ref('')
let weightTimer = null

watch(weightInput, (val) => {
  weightStatus.value = ''
  clearTimeout(weightTimer)
  // Поле показывается только до первого веса. После сохранения оно исчезает,
  // а сброс значения снова будит наблюдатель — без этой проверки прошло бы
  // второе, лишнее сохранение и второй раз записалось бы взвешивание.
  if (hasWeight.value || !val) return
  const n = parseFloat(val)
  if (!Number.isFinite(n) || n < WEIGHT_MIN || n > WEIGHT_MAX) return
  const wt = Math.round(n * 10) / 10
  weightTimer = setTimeout(async () => {
    try {
      weightStatus.value = 'Сохранение...'
      await api.post('/weight', { weight_kg: wt })
      await authStore.updateProfile({ weight_kg: wt })
      // Дальше показывается барабан — ставим его на только что введённое
      selectedWeight.value = wt.toFixed(1)
      weightInput.value = ''
      weightStatus.value = ''
      await loadHistory()
    } catch {
      weightStatus.value = 'Ошибка сохранения'
    }
  }, 800)
})

function openWeightPopup() {
  showWeightPopup.value = true
}

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
function formatDate(dateStr) {
  const d = new Date(dateStr + (dateStr.includes('T') ? '' : 'T12:00:00'))
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
}

async function loadHistory() {
  try {
    const { data } = await api.get('/weight/history', { params: { limit: 10 } })
    weightHistory.value = data.reverse()
  } catch { /* silent */ }
}

onMounted(() => {
  window.scrollTo(0, 0)
  loadHistory()
  loadFatHistory()
})

async function saveWeight() {
  const wt = weightValue.value
  if (wt === null) return
  savingWeight.value = true
  try {
    await api.post('/weight', { weight_kg: wt })
    await authStore.updateProfile({ weight_kg: wt })
    // Дальше открывается барабан — ставим его на только что введённое значение
    selectedWeight.value = wt.toFixed(1)
    weightInput.value = ''
    showWeightPopup.value = false
    await loadHistory()
  } catch { /* silent */ }
  finally { savingWeight.value = false }
}

// ── Попапы ──
const showFatCalcPopup = ref(false)
const showMethodPopup = ref(false)
let popupScrollY = 0

function lockScroll() {
  popupScrollY = window.scrollY
  document.body.style.position = 'fixed'
  document.body.style.top = `-${popupScrollY}px`
  document.body.style.left = '0'
  document.body.style.right = '0'
  document.body.style.overflow = 'hidden'
}
function unlockScroll() {
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.overflow = ''
  window.scrollTo(0, popupScrollY)
}

function openFatCalcPopup() {
  showFatCalcPopup.value = true
  lockScroll()
}
function closeFatCalcPopup() {
  showFatCalcPopup.value = false
  if (!showMethodPopup.value) unlockScroll()
}
function closeMethodPopup() {
  showMethodPopup.value = false
  if (!showFatCalcPopup.value) unlockScroll()
}

// ── Калькулятор жира (U.S. Navy) ──
const neck = ref(null)
const waist = ref(null)
const hip = ref(null)
const lastBodyFat = ref(null)
const savingFat = ref(false)
const fatHistory = ref([])

const isFemale = computed(() => authStore.user?.sex === 'female')

const canCalculate = computed(() => {
  const h = authStore.user?.height_cm || heightCm.value
  if (!h || !neck.value || !waist.value) return false
  if (neck.value <= 0 || waist.value <= 0) return false
  if (isFemale.value && (!hip.value || hip.value <= 0)) return false
  return true
})

function calcBodyFat() {
  const h = authStore.user?.height_cm || heightCm.value
  if (!h || !neck.value || !waist.value) return null

  if (isFemale.value) {
    if (!hip.value || hip.value <= 0) return null
    const val = waist.value + hip.value - neck.value
    if (val <= 0) return null
    const bf = 163.205 * Math.log10(val) - 97.684 * Math.log10(h) - 104.912
    return bf > 0 && bf < 60 ? Math.round(bf * 10) / 10 : null
  } else {
    const val = waist.value - neck.value
    if (val <= 0) return null
    const bf = 86.010 * Math.log10(val) - 70.041 * Math.log10(h) + 30.294
    return bf > 0 && bf < 60 ? Math.round(bf * 10) / 10 : null
  }
}

async function calculateAndSaveBodyFat() {
  const bf = calcBodyFat()
  if (bf === null) return
  lastBodyFat.value = bf
  savingFat.value = true
  try {
    await api.post('/weight/body-fat', { body_fat_percent: bf })
    await loadFatHistory()
    closeFatCalcPopup()
  } catch { /* silent */ }
  finally { savingFat.value = false }
}

async function loadFatHistory() {
  try {
    const { data } = await api.get('/weight/body-fat/history', { params: { limit: 10 } })
    fatHistory.value = data
    if (data.length > 0 && lastBodyFat.value === null) {
      lastBodyFat.value = parseFloat(data[0].body_fat_percent)
    }
  } catch { /* silent */ }
}
</script>

<style scoped>
.profile-body {
  min-height: 100vh;
  min-height: 100dvh;
  background: #FAF3E8;
  padding: 48px 16px 100px;
  padding-top: max(48px, calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 16px));
}



/* ── Form card (matches StepParams) ── */
.form-card {
  background: #FFFCF3;
  border-radius: 20px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 1px 4px rgba(65,41,26,0.06);
  margin-bottom: 16px;
}

.field-label {
  font-size: var(--fs-11);
  letter-spacing: 1.5px;
  font-weight: 700;
  color: var(--warm);
  text-transform: uppercase;
}

/* ── Pills (sex selector) ── */
.pill-row {
  display: flex;
  gap: 10px;
}
.pill {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 12px;
  background: transparent;
  font-size: var(--fs-15);
  font-weight: 600;
  color: var(--ink);
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.2s, background 0.2s, color 0.2s;
}
.pill.active {
  border-color: var(--ink);
  background: #41291A;
  color: var(--cream);
}

/* ── Date selects ── */
.date-selects {
  display: flex;
  gap: 6px;
}
.date-select {
  flex: 1;
  min-width: 0;
  padding: 10px 20px 10px 6px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 12px;
  background: transparent;
  font-size: var(--fs-16);
  font-weight: 500;
  color: var(--ink);
  font-family: inherit;
  appearance: none;
  -webkit-appearance: none;
  text-align: center;
  text-align-last: center;
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2341291A' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 4px center;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.date-select-month {
  flex: 1.5;
}
.date-select:focus {
  outline: none;
  border-color: var(--ink-70);
}

/* ── Form inputs ── */
.form-input {
  width: 100%;
  padding: 12px 14px;
  border: 2px solid rgba(65,41,26,0.15);
  border-radius: 12px;
  background: transparent;
  font-size: var(--fs-16);
  color: var(--ink);
  font-family: inherit;
  box-sizing: border-box;
}
.form-input:focus {
  outline: none;
  border-color: var(--ink);
}

.save-status {
  font-size: var(--fs-12);
  color: var(--ink-70);
  margin: -6px 0 0;
}

/* ── Input with button inside (weight, body fat) ── */
.input-btn-wrap {
  position: relative;
  cursor: pointer;
}
.input-btn-wrap .form-input {
  padding-right: 120px;
  cursor: pointer;
}
.input-inner-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  background: #41291A;
  color: var(--cream);
  font-size: var(--fs-14);
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

/* ── History rows ── */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.history-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(65,41,26,0.04);
}
.history-value {
  font-size: var(--fs-14);
  font-weight: 600;
  color: var(--ink);
}
.history-date {
  font-size: var(--fs-13);
  color: var(--ink-70);
}

/* ── Body fat result ── */
.info-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.calc-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
}
.calc-result {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.field-label-with-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fat-result {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.fat-result-value {
  font-size: var(--fs-28);
  font-weight: 700;
  color: var(--ink);
}
/* ── Primary button ── */


.btn-back {
  margin-top: 4px;
}

/* ── Popup (method + weight) ── */
.popup-overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.popup-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.4);
}
.popup-sheet {
  position: relative;
  width: 100%;
  max-width: 430px;
  background: #FAF3E8;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 24px rgba(0,0,0,0.12);
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}
.popup-sheet-compact {
  max-height: none;
  padding: 16px 20px 32px;
}
.popup-handle {
  width: 40px;
  height: 4px;
  background: #D9D0C4;
  border-radius: 4px;
  margin: 16px auto 12px;
}
.popup-header {
  flex-shrink: 0;
  padding: 12px 20px 12px;
}
.popup-header .popup-handle {
  margin-top: 0;
}
.popup-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.popup-title {
  font-size: var(--fs-17);
  font-weight: 700;
  color: var(--ink);
}
.popup-close {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: var(--ink);
  background: none;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 8px;
}
.popup-close:active {
  background: #E8E2D9;
}
.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 20px 32px;
}
.popup-text {
  font-size: var(--fs-14);
  color: var(--ink);
  line-height: 1.6;
}
.popup-text p {
  margin-bottom: 12px;
}
.popup-text ul {
  list-style: disc;
  padding-left: 20px;
  margin-bottom: 12px;
}
.popup-text li {
  margin-bottom: 4px;
}
.popup-subtitle {
  font-size: var(--fs-16);
  font-weight: 600;
  padding-top: 4px;
}
.popup-text .btn-primary {
  margin-top: 16px;
}

/* Weight popup */
.weight-popup-title {
  font-size: var(--fs-16);
  font-weight: 500;
  color: var(--ink);
  text-align: center;
  margin-bottom: 12px;
}
.weight-input-row {
  margin-bottom: 16px;
}
.weight-input {
  text-align: center;
  font-size: var(--fs-20);
  font-weight: 600;
}
.weight-error {
  font-size: var(--fs-12);
  color: var(--error);
  text-align: center;
  margin: 6px 0 0;
}
.weight-picker-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}
.weight-picker-unit {
  font-size: var(--fs-20);
  font-weight: 600;
  color: var(--ink);
}

/* ── Transitions ── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.btn-primary { margin-top: 16px; }
</style>
